import { Marker } from 'maplibre-gl';
import { MODES, routeVehicle, routeTransit, connectionGeometry } from '../data/routing.js';
import { geocode } from './searchBox.js';
import { toast } from './toast.js';

const EMPTY = { type: 'FeatureCollection', features: [] };

function endpointMarker(letter) {
  const el = document.createElement('div');
  el.textContent = letter;
  el.style.cssText = `display:grid;place-items:center;width:20px;height:20px;background:#111;
    color:#fcfcfc;font:700 12px/1 'Iosevka KGV',ui-monospace,monospace;cursor:grab`;
  return el;
}

const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' }) : '');

export function createRoutePanel({ map, onActivate, onStartNavigation }) {
  const points = { from: null, to: null };
  const markers = { from: null, to: null };
  let mode = 'auto';
  let controller;
  let nodes = null;
  let connections = [];
  let drawn = null;   // zuletzt gezeichnete Route, für den Style-Wechsel

  /* --------------------------------------------------------- Kartenebenen */

  function ensureLayers() {
    if (map.getSource('route')) return;
    map.addSource('route', { type: 'geojson', data: EMPTY });
    map.addSource('route-stops', { type: 'geojson', data: EMPTY });
    map.addLayer({
      id: 'route-casing',
      type: 'line',
      source: 'route',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#FCFCFC', 'line-width': ['interpolate', ['linear'], ['zoom'], 8, 5, 14, 9, 18, 14] }
    });
    map.addLayer({
      id: 'route-line',
      type: 'line',
      source: 'route',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#141414',
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 14, 4.5, 18, 7],
        'line-dasharray': ['case', ['==', ['get', 'walk'], true], ['literal', [2, 1.5]], ['literal', [1, 0]]]
      }
    });
    map.addLayer({
      id: 'route-stops-layer',
      type: 'circle',
      source: 'route-stops',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 2.5, 14, 4],
        'circle-color': '#FCFCFC',
        'circle-stroke-color': '#141414',
        'circle-stroke-width': 1.5
      }
    });
  }

  function drawRoute(features, stops = []) {
    drawn = { features, stops };
    ensureLayers();
    map.getSource('route').setData({ type: 'FeatureCollection', features });
    map.getSource('route-stops').setData({
      type: 'FeatureCollection',
      features: stops.map((c) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: {} }))
    });
    const all = features.flatMap((f) => f.geometry.coordinates);
    if (all.length > 1) {
      const lngs = all.map((c) => c[0]);
      const lats = all.map((c) => c[1]);
      map.fitBounds(
        [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
        { padding: 70, duration: 700 }
      );
    }
  }

  function clearRoute() {
    drawn = null;
    if (!map.getSource('route')) return;
    map.getSource('route').setData(EMPTY);
    map.getSource('route-stops').setData(EMPTY);
  }

  /* -------------------------------------------------------------- Punkte */

  function setPoint(which, point) {
    points[which] = point;
    markers[which]?.remove();
    markers[which] = null;
    if (point) {
      markers[which] = new Marker({ element: endpointMarker(which === 'from' ? 'A' : 'B'), draggable: true })
        .setLngLat([point.lng, point.lat])
        .addTo(map);
      markers[which].on('dragend', () => {
        const ll = markers[which].getLngLat();
        points[which] = { lat: ll.lat, lng: ll.lng, name: `${ll.lat.toFixed(4)}, ${ll.lng.toFixed(4)}` };
        paintFields();
        run();
      });
    }
    paintFields();
    onActivate?.();
    run();
  }

  function paintFields() {
    if (!nodes) return;
    nodes.fromInput.value = points.from?.name || '';
    nodes.toInput.value = points.to?.name || '';
  }

  /* ------------------------------------------------------------- Rechnen */

  async function run() {
    if (!nodes) return;
    if (!points.from || !points.to) {
      nodes.result.textContent = '';
      clearRoute();
      return;
    }
    controller?.abort();
    controller = new AbortController();
    const signal = controller.signal;

    nodes.result.textContent = '';
    const busy = document.createElement('p');
    busy.className = 'hint hint--block';
    busy.textContent = 'Berechne …';
    nodes.result.append(busy);

    try {
      if (mode === 'transit') {
        const res = await routeTransit({ from: points.from, to: points.to, signal });
        connections = res.connections;
        renderTransit();
        selectConnection(0);
      } else {
        const costing = MODES.find((m) => m.id === mode).costing;
        const res = await routeVehicle({ from: points.from, to: points.to, costing, signal });
        drawRoute([{ type: 'Feature', geometry: { type: 'LineString', coordinates: res.coordinates }, properties: {} }]);
        renderVehicle(res);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      nodes.result.textContent = '';
      const p = document.createElement('p');
      p.className = 'error';
      p.textContent = err.message;
      nodes.result.append(p);
      clearRoute();
    }
  }

  /* ----------------------------------------------------------- Ausgaben */

  function summaryLine(main, sub) {
    const box = document.createElement('div');
    box.className = 'route__summary';
    const a = document.createElement('strong');
    a.textContent = main;
    const b = document.createElement('span');
    b.textContent = sub;
    box.append(a, b);
    return box;
  }

  function renderVehicle(res) {
    nodes.result.textContent = '';

    // Zielfuehrung nur, wo sie Sinn ergibt: mit Manoevern und mit Ortung.
    if (onStartNavigation && res.maneuvers?.length && navigator.geolocation) {
      const los = document.createElement('button');
      los.type = 'button';
      los.className = 'route__go';
      los.textContent = 'Navigation starten';
      los.addEventListener('click', () => {
        onStartNavigation({
          route: res,
          to: points.to,
          costing: MODES.find((m) => m.id === mode).costing
        });
      });
      nodes.result.append(los);
    }

    nodes.result.append(
      summaryLine(
        `${res.durationMin} Min`,
        `${res.distanceKm.toFixed(res.distanceKm < 10 ? 1 : 0)} km · ${MODES.find((m) => m.id === mode).label}`
      )
    );
    const list = document.createElement('ol');
    list.className = 'route__steps';
    for (const s of res.steps) {
      const li = document.createElement('li');
      const t = document.createElement('span');
      t.textContent = s.text;
      const d = document.createElement('em');
      d.textContent = s.km >= 1 ? `${s.km.toFixed(1)} km` : `${Math.round(s.km * 1000)} m`;
      li.append(t, d);
      list.append(li);
    }
    nodes.result.append(list);
  }

  function renderTransit() {
    nodes.result.textContent = '';
    const list = document.createElement('div');
    list.className = 'route__connections';
    connections.forEach((c, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'route__conn';
      btn.dataset.index = String(i);

      const head = document.createElement('strong');
      head.textContent = `${hhmm(c.departure)} – ${hhmm(c.arrival)}`;
      const meta = document.createElement('span');
      meta.textContent = [
        c.duration != null ? `${c.duration} Min` : null,
        c.transfers ? `${c.transfers} Umstiege` : 'direkt'
      ].filter(Boolean).join(' · ');
      const lines = document.createElement('span');
      lines.className = 'route__lines';
      lines.textContent = c.legs.filter((l) => l.line).map((l) => l.line).join(' › ') || 'Fussweg';

      btn.append(head, meta, lines);
      btn.addEventListener('click', () => selectConnection(i));
      list.append(btn);
    });
    nodes.result.append(list);
  }

  function selectConnection(index) {
    const c = connections[index];
    if (!c) return;
    for (const b of nodes.result.querySelectorAll('.route__conn')) {
      b.classList.toggle('is-active', Number(b.dataset.index) === index);
    }
    const features = c.legs
      .filter((l) => l.coordinates.length > 1)
      .map((l) => ({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: l.coordinates },
        properties: { walk: l.walk }
      }));
    const stops = connectionGeometry(c);
    if (features.length) drawRoute(features, stops);
  }

  /* ---------------------------------------------------------------- Mount */

  function mount(n) {
    nodes = n;

    for (const m of MODES) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', String(m.id === mode));
      btn.textContent = m.label;
      btn.addEventListener('click', () => {
        mode = m.id;
        for (const other of n.modeHost.children) other.setAttribute('aria-checked', 'false');
        btn.setAttribute('aria-checked', 'true');
        run();
      });
      n.modeHost.append(btn);
    }

    const lookup = async (which, input) => {
      const q = input.value.trim();
      if (!q) return setPoint(which, null);
      try {
        setPoint(which, await geocode(q, map));
      } catch (err) {
        toast(err.message);
      }
    };
    /**
     * Eigener Standort als Routenpunkt. Die Ortung kann dauern und
     * fehlschlagen - solange laeuft der Knopf sichtbar, danach steht
     * entweder der Punkt oder eine Meldung.
     */
    function standortUebernehmen(which, knopf) {
      if (!navigator.geolocation) return;
      const vorher = knopf.textContent;
      knopf.textContent = '…';
      knopf.disabled = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          knopf.textContent = vorher;
          knopf.disabled = false;
          setPoint(which, {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            name: 'Mein Standort'
          });
        },
        () => {
          knopf.textContent = vorher;
          knopf.disabled = false;
          knopf.title = 'Standort nicht verfügbar';
        },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
      );
    }
    n.hereFrom?.addEventListener('click', () => standortUebernehmen('from', n.hereFrom));
    n.hereTo?.addEventListener('click', () => standortUebernehmen('to', n.hereTo));

    n.fromInput.addEventListener('change', () => lookup('from', n.fromInput));
    n.toInput.addEventListener('change', () => lookup('to', n.toInput));

    n.swapBtn.addEventListener('click', () => {
      const a = points.from;
      setPoint('from', points.to);
      setPoint('to', a);
    });

    n.clearBtn.addEventListener('click', () => {
      setPoint('from', null);
      setPoint('to', null);
      connections = [];
      nodes.result.textContent = '';
      clearRoute();
    });
  }

  /** setStyle wirft eigene Quellen und Ebenen weg - hier kommen sie zurück. */
  function redraw() {
    if (!drawn) return;
    const { features, stops } = drawn;
    ensureLayers();
    map.getSource('route').setData({ type: 'FeatureCollection', features });
    map.getSource('route-stops').setData({
      type: 'FeatureCollection',
      features: stops.map((c) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: {} }))
    });
  }

  return { mount, setPoint, redraw, hasPoint: (which) => Boolean(points[which]) };
}
